import { useCallback, useState } from 'react'

import { describeError, fieldOfError } from '../lib/api/messages.ts'
import { companies as platformCompanies, users as platformUsers } from '../lib/api/resources.ts'
import { tenant } from '../lib/api/tenant.ts'
import { PLATFORM_ROLES, ROLES, TENANT_ROLES } from '../lib/api/types.ts'
import type {
  AthenaRole,
  AthenaScope,
  CompanyResponse,
  CreateTenantUserRequest,
  CreateUserRequest,
  PagedResponse,
  UpdateUserRequest,
  UserQuery,
  UserResponse,
} from '../lib/api/types.ts'
import { useAuth } from '../lib/auth/AuthContext.tsx'
import { formatDayClock } from '../lib/format.ts'
import { useAction } from '../lib/useAction.ts'
import { useDebounced } from '../lib/useDebounced.ts'
import { useResource } from '../lib/useResource.ts'
import { CompanySelect, companyNameOf } from '../components/companies/CompanySelect.tsx'
import type { useCompanyList } from '../components/companies/CompanySelect.tsx'
import { PageHeader, Toolbar } from '../components/layout/PageHeader.tsx'
import { Badge, ROLE_LABEL, RoleBadge } from '../components/primitives/Badge.tsx'
import { Button } from '../components/primitives/Button.tsx'
import { ConfirmDialog, FormDialog } from '../components/primitives/Dialog.tsx'
import { CheckboxField, FilterToggle, SearchInput, SelectField, TextField } from '../components/primitives/Field.tsx'
import { Menu, MenuItem, MenuSeparator } from '../components/primitives/Menu.tsx'
import { Pagination } from '../components/primitives/Pagination.tsx'
import { EmptyState, ErrorState, LoadingRow } from '../components/primitives/States.tsx'
import { TABLE, TD, TH, THEAD, TR } from '../components/primitives/table.ts'
import { useToast } from '../components/primitives/Toast.tsx'

const PAGE_SIZE = 50

type Dialog =
  | { kind: 'create' }
  | { kind: 'edit'; user: UserResponse }
  | { kind: 'toggle'; user: UserResponse }
  | { kind: 'password'; user: UserResponse }
  | { kind: 'delete'; user: UserResponse }
  | null

/**
 * As rotas de usuários de cada experiência. Mesmo DTO (`UserResponse`, camelCase)
 * nas duas pontas; muda a superfície e o que ela aceita.
 *
 *  - `platform` → `/api/v1/users` (`platform_admin`): todas as empresas, com
 *    filtros de escopo e de empresa, e criação dos dois tipos de usuário.
 *  - `tenant` → `/api/tenant/users` (`tenant_admin`): só a própria empresa; a
 *    empresa vem do token e o corpo nunca leva `companyId` (§5.10).
 */
interface UsersApi {
  list: (params: UserQuery, signal?: AbortSignal) => Promise<PagedResponse<UserResponse>>
  update: (id: string, body: UpdateUserRequest) => Promise<UserResponse>
  resetPassword: (id: string) => Promise<void>
  remove: (id: string) => Promise<void>
}

const PLATFORM_API: UsersApi = platformUsers

const TENANT_API: UsersApi = {
  // Campo a campo: `companyId`/`scope` de um filtro nunca vão a /api/tenant.
  list: (params, signal) =>
    tenant.users.list(
      {
        page: params.page,
        pageSize: params.pageSize,
        search: params.search,
        role: params.role,
        includeInactive: params.includeInactive,
        includeDeleted: params.includeDeleted,
      },
      signal,
    ),
  update: tenant.users.update,
  resetPassword: tenant.users.resetPassword,
  remove: tenant.users.remove,
}

/** Papéis oferecidos por escopo: o backend recusa misturar (`roles_scope_mixed`). */
function rolesOf(scope: AthenaScope): readonly AthenaRole[] {
  return scope === 'platform' ? PLATFORM_ROLES : TENANT_ROLES
}

function adminRoleOf(scope: AthenaScope): AthenaRole {
  return scope === 'platform' ? 'platform_admin' : 'tenant_admin'
}

/** Escopo do usuário listado; `scope` ausente (resposta antiga) cai pelos papéis. */
function scopeOfUser(user: UserResponse): AthenaScope {
  return user.scope ?? (user.roles.some((role) => role.startsWith('tenant_')) ? 'tenant' : 'platform')
}

/**
 * Usuários.
 *
 * Na Black Ghost a tela exige `platform_admin`, e a exigência não é burocracia:
 * quem pode criar usuário pode criar o próprio administrador paralelo. Isso não
 * pode ser efeito colateral de conseguir emitir uma licença. No cliente, o
 * `tenant_admin` gerencia só os usuários da própria empresa.
 *
 * As duas proteções contra o administrador se trancar para fora — desativar a
 * própria conta e remover o próprio papel — são impedidas aqui **antes** do
 * envio. O servidor recusa de qualquer forma (`cannot_deactivate_self`,
 * `cannot_drop_own_admin_role`); mostrar o botão desabilitado com o motivo é
 * melhor do que deixar o operador descobrir pelo erro.
 */
export function Users({ mode = 'platform' }: { mode?: AthenaScope }) {
  const { user: current } = useAuth()
  const platform = mode === 'platform'
  const api = platform ? PLATFORM_API : TENANT_API

  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const search = useDebounced(searchInput)
  const [role, setRole] = useState<AthenaRole | ''>('')
  const [scopeFilter, setScopeFilter] = useState<AthenaScope | ''>('')
  const [companyFilter, setCompanyFilter] = useState('')
  const [includeInactive, setIncludeInactive] = useState(true)
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [dialog, setDialog] = useState<Dialog>(null)

  const fetcher = useCallback(
    (signal: AbortSignal) =>
      api.list(
        {
          page,
          pageSize: PAGE_SIZE,
          search,
          role: role === '' ? undefined : role,
          includeInactive,
          includeDeleted,
          // Só na Black Ghost; a API de cliente os descarta de todo jeito.
          scope: platform && scopeFilter !== '' ? scopeFilter : undefined,
          companyId: platform && companyFilter !== '' ? companyFilter : undefined,
        },
        signal,
      ),
    [api, platform, page, search, role, scopeFilter, companyFilter, includeInactive, includeDeleted],
  )

  const { data, error, loading, reload } = useResource(fetcher)
  // `/api/v1/companies` é da Black Ghost: o cliente nunca a chama (403).
  const companyList = usePlatformCompanyList(platform)

  const close = useCallback(() => setDialog(null), [])
  const refresh = useCallback(() => {
    setDialog(null)
    reload()
  }, [reload])

  return (
    <>
      <PageHeader
        title="Usuários"
        actions={
          <Button variant="primary" onClick={() => setDialog({ kind: 'create' })}>
            Novo usuário
          </Button>
        }
      />

      <Toolbar>
        <SearchInput
          label="Buscar usuário"
          placeholder="login, e-mail ou nome"
          value={searchInput}
          onValueChange={(value) => {
            setSearchInput(value)
            setPage(1)
          }}
          className="w-64"
        />
        <label className="flex items-center gap-1 text-micro uppercase tracking-label text-fg-muted">
          papel
          <select
            value={role}
            onChange={(event) => {
              setRole(event.target.value as AthenaRole | '')
              setPage(1)
            }}
            className="border border-line-strong bg-inset px-1.5 py-0.5 text-sm text-fg"
          >
            <option value="">todos</option>
            {(platform ? ROLES : TENANT_ROLES).map((option) => (
              <option key={option} value={option}>
                {ROLE_LABEL[option]}
              </option>
            ))}
          </select>
        </label>
        {platform && (
          <label className="flex items-center gap-1 text-micro uppercase tracking-label text-fg-muted">
            tipo
            <select
              value={scopeFilter}
              onChange={(event) => {
                setScopeFilter(event.target.value as AthenaScope | '')
                setPage(1)
              }}
              className="border border-line-strong bg-inset px-1.5 py-0.5 text-sm text-fg"
            >
              <option value="">todos</option>
              <option value="platform">Black Ghost</option>
              <option value="tenant">clientes</option>
            </select>
          </label>
        )}
        {platform && (
          <CompanySelect
            list={companyList}
            value={companyFilter}
            onChange={(value) => {
              setCompanyFilter(value)
              setPage(1)
            }}
            allLabel="Todas as empresas"
          />
        )}
        <FilterToggle
          label="inativos"
          pressed={includeInactive}
          onPressedChange={(value) => {
            setIncludeInactive(value)
            setPage(1)
          }}
        />
        <FilterToggle
          label="removidos"
          pressed={includeDeleted}
          onPressedChange={(value) => {
            setIncludeDeleted(value)
            setPage(1)
          }}
        />
        {loading && <span className="text-micro text-fg-muted">carregando…</span>}
      </Toolbar>

      <div className="min-h-0 flex-1 overflow-auto">
        {error !== null ? (
          <ErrorState error={error} onRetry={reload} />
        ) : data === null ? (
          <LoadingRow />
        ) : data.items.length === 0 ? (
          <EmptyState title="Nenhum usuário encontrado." />
        ) : (
          <table className={TABLE}>
            <thead className={THEAD}>
              <tr>
                <th scope="col" className={TH}>
                  Login
                </th>
                <th scope="col" className={TH}>
                  Nome
                </th>
                <th scope="col" className={TH}>
                  E-mail
                </th>
                {platform && (
                  <th scope="col" className={TH}>
                    Empresa
                  </th>
                )}
                <th scope="col" className={TH}>
                  Papéis
                </th>
                <th scope="col" className={TH}>
                  Situação
                </th>
                <th scope="col" className={TH}>
                  Último acesso
                </th>
                <th scope="col" className={`${TH} text-right`}>
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((user) => {
                const isSelf = user.id === current?.id
                const userScope = scopeOfUser(user)

                return (
                  <tr key={user.id} className={TR}>
                    <th scope="row" className={`${TD} font-mono font-normal whitespace-nowrap text-fg`}>
                      {user.username}
                      {isSelf && <span className="ml-1.5 text-micro text-fg-muted">você</span>}
                    </th>
                    <td className={`${TD} max-w-0`}>
                      <span className="block truncate">{user.displayName}</span>
                    </td>
                    <td className={`${TD} max-w-0`}>
                      <span className="block truncate" title={user.email}>
                        {user.email}
                      </span>
                    </td>
                    {platform && (
                      <td className={`${TD} max-w-0`}>
                        {userScope === 'platform' ? (
                          <Badge tone="accent">Black Ghost</Badge>
                        ) : (
                          <span className="block truncate">
                            {companyNameOf(companyList.data, user.companyId) ?? (
                              <span className="italic text-fg-muted">sem empresa</span>
                            )}
                          </span>
                        )}
                      </td>
                    )}
                    <td className={TD}>
                      <span className="flex flex-wrap gap-1">
                        {user.roles.map((item) => (
                          <RoleBadge key={item} role={item} />
                        ))}
                      </span>
                    </td>
                    <td className={TD}>
                      <span className="flex flex-wrap gap-1">
                        {user.deletedAt !== null ? (
                          <Badge tone="crit">removido</Badge>
                        ) : user.isActive ? (
                          <Badge tone="ok" dot>
                            ativo
                          </Badge>
                        ) : (
                          <Badge tone="warn" dot>
                            desativado
                          </Badge>
                        )}
                        {user.mustChangePassword && (
                          <Badge tone="accent">senha provisória</Badge>
                        )}
                        {user.isLockedOut && (
                          <Badge
                            tone="warn"
                            title="Cinco senhas erradas. Expira em 15 minutos."
                          >
                            bloqueado
                          </Badge>
                        )}
                      </span>
                    </td>
                    <td className={`${TD} tnum whitespace-nowrap`}>
                      {formatDayClock(user.lastLoginAt)}
                    </td>
                    <td className={`${TD} text-right`}>
                      {user.deletedAt === null && (
                        <Menu trigger={<span>ações</span>} label={`Ações de ${user.username}`}>
                          <MenuItem onSelect={() => setDialog({ kind: 'edit', user })}>
                            Editar
                          </MenuItem>
                          <MenuItem onSelect={() => setDialog({ kind: 'password', user })}>
                            Redefinir senha
                          </MenuItem>
                          <MenuSeparator />
                          {!isSelf && (
                            <MenuItem
                              danger={user.isActive}
                              onSelect={() => setDialog({ kind: 'toggle', user })}
                            >
                              {user.isActive ? 'Desativar' : 'Reativar'}
                            </MenuItem>
                          )}
                          {!isSelf && (
                            <MenuItem danger onSelect={() => setDialog({ kind: 'delete', user })}>
                              Remover
                            </MenuItem>
                          )}
                        </Menu>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {data !== null && data.items.length > 0 && (
        <Pagination
          page={data.page}
          pageSize={data.pageSize}
          totalCount={data.totalCount}
          totalPages={data.totalPages}
          busy={loading}
          onPageChange={setPage}
        />
      )}

      {dialog?.kind === 'create' && (
        <CreateDialog
          mode={mode}
          companyList={platform ? companyList : null}
          onClose={close}
          onCreated={refresh}
        />
      )}

      {dialog?.kind === 'edit' && (
        <EditDialog
          api={api}
          user={dialog.user}
          isSelf={dialog.user.id === current?.id}
          onClose={close}
          onSaved={refresh}
        />
      )}

      {dialog?.kind === 'toggle' && (
        <ToggleDialog api={api} user={dialog.user} onClose={close} onSaved={refresh} />
      )}

      {dialog?.kind === 'password' && (
        <ResetPasswordDialog api={api} user={dialog.user} onClose={close} onSaved={refresh} />
      )}

      {dialog?.kind === 'delete' && (
        <DeleteDialog api={api} user={dialog.user} onClose={close} onDeleted={refresh} />
      )}
    </>
  )
}

const ROLE_HINT: Record<AthenaRole, string> = {
  platform_staff: 'Empresas, máquinas, licenças, agentes e o painel ao vivo.',
  platform_admin: 'Tudo do operador, mais o cadastro de usuários.',
  tenant_user: 'O painel da empresa, somente leitura.',
  tenant_admin: 'Tudo do usuário da empresa, mais o cadastro de usuários.',
}

/**
 * Os papéis de UM escopo. Nunca os dois: o backend recusa misturar
 * (`roles_scope_mixed`), e a empresa de um usuário é imutável — então a edição
 * também não troca de escopo.
 */
function RolesField({
  scope,
  value,
  onChange,
  error,
  lockAdmin = false,
}: {
  scope: AthenaScope
  value: AthenaRole[]
  onChange: (roles: AthenaRole[]) => void
  error?: string | null
  /** Impede remover o próprio papel de administrador — o servidor recusaria de todo jeito. */
  lockAdmin?: boolean
}) {
  const toggle = (role: AthenaRole, checked: boolean): void => {
    onChange(checked ? [...new Set([...value, role])] : value.filter((item) => item !== role))
  }
  const adminRole = adminRoleOf(scope)
  // O papel comum primeiro, o administrador depois.
  const ordered = [...rolesOf(scope)].sort((a, b) => Number(a === adminRole) - Number(b === adminRole))

  return (
    <fieldset className="flex min-w-0 flex-col gap-1">
      <legend className="text-micro font-medium uppercase tracking-label text-fg-secondary">
        Papéis
      </legend>
      {/* PUT: o conjunto marcado é o FINAL, não um acréscimo. Desmarcar
          administrador em quem já era remove o papel. */}
      {ordered.map((role) => {
        const locked = role === adminRole && lockAdmin && value.includes(role)
        return (
          <CheckboxField
            key={role}
            label={ROLE_LABEL[role]}
            checked={value.includes(role)}
            onCheckedChange={(checked) => toggle(role, checked)}
            disabled={locked}
            hint={
              locked
                ? 'Você não pode remover o próprio papel de administrador.'
                : ROLE_HINT[role]
            }
          />
        )
      })}
      {error != null && error !== '' && <p className="text-micro text-crit">{error}</p>}
    </fieldset>
  )
}

/**
 * Criação. Na Black Ghost o operador escolhe o TIPO primeiro: "Black Ghost" usa
 * papéis `platform_*` e nenhuma empresa; "Cliente" usa `tenant_*` e exige a
 * empresa — imutável depois. No cliente não há tipo nem empresa: o usuário vai
 * sempre para a empresa do token.
 */
function CreateDialog({
  mode,
  companyList,
  onClose,
  onCreated,
}: {
  mode: AthenaScope
  /** Só na Black Ghost. */
  companyList: ReturnType<typeof useCompanyList> | null
  onClose: () => void
  onCreated: () => void
}) {
  const toast = useToast()
  const action = useAction()
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [kind, setKind] = useState<AthenaScope>(mode)
  const [companyId, setCompanyId] = useState('')
  const [roles, setRoles] = useState<AthenaRole[]>([mode === 'platform' ? 'platform_staff' : 'tenant_user'])

  const changeKind = (next: AthenaScope): void => {
    setKind(next)
    // Papéis de um escopo não sobrevivem à troca: misturar é `roles_scope_mixed`.
    setRoles([next === 'platform' ? 'platform_staff' : 'tenant_user'])
    if (next === 'platform') setCompanyId('')
  }

  const field = fieldOfError(action.error)
  const message = describeError(action.error)

  return (
    <FormDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title="Novo usuário"
      submitLabel="Criar usuário"
      busy={action.busy}
      error={field === null && action.error !== null ? message : null}
      onSubmit={() =>
        action.run(async () => {
          const base: CreateTenantUserRequest = {
            username,
            email,
            displayName: displayName === '' ? null : displayName,
            roles,
          }
          let created: UserResponse
          if (mode === 'tenant') {
            created = await tenant.users.create(base)
          } else {
            // `companyId` só com papéis `tenant_*`; vazio vai ausente e o servidor
            // responde `company_required_for_tenant_roles`, que marca o campo.
            const body: CreateUserRequest =
              kind === 'tenant' && companyId !== '' ? { ...base, companyId } : base
            created = await platformUsers.create(body)
          }
          toast.success(`Usuário ${created.username} criado. As credenciais foram enviadas para ${created.email}.`)
          onCreated()
        })
      }
    >
      {mode === 'platform' && (
        <SelectField
          label="Tipo de usuário"
          value={kind}
          onValueChange={(value) => changeKind(value as AthenaScope)}
          options={[
            { value: 'platform', label: 'Black Ghost — equipe interna, sem empresa' },
            { value: 'tenant', label: 'Cliente — usuário de uma empresa' },
          ]}
        />
      )}
      {mode === 'platform' && kind === 'tenant' && companyList !== null && (
        <CompanySelect
          list={companyList}
          value={companyId}
          onChange={setCompanyId}
          placeholder="Escolha a empresa"
          error={field === 'companyId' ? message : null}
          hint="Imutável depois de criado."
          className="w-full"
        />
      )}
      <TextField
        label="Login"
        value={username}
        onValueChange={setUsername}
        error={field === 'username' ? message : null}
        hint="a-z, 0-9, ponto, hífen e sublinhado. Imutável."
        maxLength={64}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        required
        mono
        autoFocus
      />
      <TextField
        label="E-mail"
        type="email"
        value={email}
        onValueChange={setEmail}
        error={field === 'email' ? message : null}
        maxLength={254}
        required
      />
      <TextField
        label="Nome de exibição"
        value={displayName}
        onValueChange={setDisplayName}
        error={field === 'displayName' ? message : null}
        hint="Em branco, assume o login."
        maxLength={128}
      />
      <RolesField
        scope={kind}
        value={roles}
        onChange={setRoles}
        error={field === 'roles' ? message : null}
      />
    </FormDialog>
  )
}

function EditDialog({
  api,
  user,
  isSelf,
  onClose,
  onSaved,
}: {
  api: UsersApi
  user: UserResponse
  isSelf: boolean
  onClose: () => void
  onSaved: () => void
}) {
  const toast = useToast()
  const action = useAction()
  const [email, setEmail] = useState(user.email)
  const [displayName, setDisplayName] = useState(user.displayName)
  const [roles, setRoles] = useState<AthenaRole[]>(user.roles)

  const field = fieldOfError(action.error)
  const message = describeError(action.error)

  return (
    <FormDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title={`Editar ${user.username}`}
      submitLabel="Salvar"
      busy={action.busy}
      error={field === null && action.error !== null ? message : null}
      onSubmit={() =>
        action.run(async () => {
          // `isActive` vai com o valor atual: o PUT substitui o recurso, e
          // desativar tem confirmação própria por causa da consequência.
          await api.update(user.id, {
            email,
            displayName: displayName === '' ? null : displayName,
            roles,
            isActive: user.isActive,
          })
          toast.success(`Usuário ${user.username} atualizado.`)
          onSaved()
        })
      }
    >
      <TextField
        label="E-mail"
        type="email"
        value={email}
        onValueChange={setEmail}
        error={field === 'email' ? message : null}
        maxLength={254}
        required
        autoFocus
      />
      <TextField
        label="Nome de exibição"
        value={displayName}
        onValueChange={setDisplayName}
        error={field === 'displayName' ? message : null}
        maxLength={128}
      />
      <RolesField
        scope={scopeOfUser(user)}
        value={roles}
        onChange={setRoles}
        error={field === 'roles' ? message : null}
        lockAdmin={isSelf}
      />
    </FormDialog>
  )
}

function ToggleDialog({
  api,
  user,
  onClose,
  onSaved,
}: {
  api: UsersApi
  user: UserResponse
  onClose: () => void
  onSaved: () => void
}) {
  const toast = useToast()
  const action = useAction()
  const deactivating = user.isActive

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title={deactivating ? `Desativar ${user.username}?` : `Reativar ${user.username}?`}
      consequence={
        deactivating
          ? 'As sessões do usuário caem em todos os dispositivos.'
          : 'A conta volta a poder entrar.'
      }
      confirmLabel={deactivating ? 'Desativar' : 'Reativar'}
      destructive={deactivating}
      busy={action.busy}
      error={action.error !== null ? describeError(action.error) : null}
      onConfirm={() =>
        action.run(async () => {
          await api.update(user.id, {
            email: user.email,
            displayName: user.displayName,
            roles: user.roles,
            isActive: !user.isActive,
          })
          toast.success(
            deactivating ? `Usuário ${user.username} desativado.` : `Usuário ${user.username} reativado.`,
          )
          onSaved()
        })
      }
    />
  )
}

function ResetPasswordDialog({
  api,
  user,
  onClose,
  onSaved,
}: {
  api: UsersApi
  user: UserResponse
  onClose: () => void
  onSaved: () => void
}) {
  const toast = useToast()
  const action = useAction()

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title={`Redefinir a senha de ${user.username}?`}
      consequence={`Uma senha provisória será enviada para ${user.email}. As sessões dele caem.`}
      confirmLabel="Redefinir e enviar"
      destructive
      busy={action.busy}
      error={action.error !== null ? describeError(action.error) : null}
      onConfirm={() =>
        action.run(async () => {
          await api.resetPassword(user.id)
          toast.success(`Senha de ${user.username} redefinida. A provisória foi enviada para ${user.email}.`)
          onSaved()
        })
      }
    />
  )
}

function DeleteDialog({
  api,
  user,
  onClose,
  onDeleted,
}: {
  api: UsersApi
  user: UserResponse
  onClose: () => void
  onDeleted: () => void
}) {
  const toast = useToast()
  const action = useAction()

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title={`Remover ${user.username}?`}
      consequence="As sessões caem. O login e o e-mail ficam livres."
      confirmLabel="Remover"
      busy={action.busy}
      error={action.error !== null ? describeError(action.error) : null}
      onConfirm={() =>
        action.run(async () => {
          await api.remove(user.id)
          toast.success(`Usuário ${user.username} removido.`)
          onDeleted()
        })
      }
    />
  )
}

const NO_COMPANIES: PagedResponse<CompanyResponse> = {
  items: [],
  page: 1,
  pageSize: 0,
  totalCount: 0,
  totalPages: 0,
}

/**
 * A lista de empresas só na Black Ghost. O hook é chamado sempre (regra dos
 * hooks), mas no cliente devolve uma lista vazia sem tocar `/api/v1/companies`.
 */
function usePlatformCompanyList(enabled: boolean): ReturnType<typeof useCompanyList> {
  const fetcher = useCallback(
    (signal: AbortSignal) =>
      enabled
        ? platformCompanies.list({ page: 1, pageSize: 200 }, signal)
        : Promise.resolve(NO_COMPANIES),
    [enabled],
  )
  return useResource(fetcher)
}
